// Data seam for the first-run onboarding. The guided three-step
// flow the owner of a new tenant sees before their assistant is live:
// bienvenida -> conectar WhatsApp -> salir en vivo. Concierge tone throughout —
// the account manager is named and present on every step.
//
// Wired:
//   GET  /onboarding            -> whether first-run is still due, owner name,
//                                  account manager, assistant summary + the
//                                  connected WhatsApp number/status.
//   POST /onboarding/activate   -> stamps the first-run flag. It does NOT
//                                  publish the agent; see activateAssistant.
// The first-run gate (whether onboarding shows at all) is the server's `pending`
// flag: tenants.status still 'onboarding' AND this user's
// onboarding_completed_at IS NULL.
// "Ver después" navigates to /home page-side without a backend call.

import { apiClient } from '../../api/client.js'

export interface OnboardingManager {
  name: string
  initials: string
}

export interface SetupItem {
  /** icon is chosen by id in the component */
  id: 'whatsapp' | 'assistant' | 'golive'
  tone: 'blue' | 'ink' | 'ok'
  title: string
  description: string
}

export interface WhatsAppConnection {
  number: string
  verifiedNote: string
}

export interface SummaryRow {
  id: string
  label: string
  value: string
  /** Render the value with a connected check (the WhatsApp number row). */
  connected?: boolean
}

export interface OnboardingData {
  /**
   * Is the first-run wizard still due? False once the workspace has gone live
   * (tenants.status leaves 'onboarding') or this user already finished it. The
   * page sends them to /home rather than rendering a flow they cannot complete.
   */
  pending: boolean
  ownerName: string
  manager: OnboardingManager
  setupItems: SetupItem[]
  whatsapp: WhatsAppConnection
  summary: SummaryRow[]
}

/**
 * GET /onboarding — the first-run wizard's data: owner name, account manager,
 * assistant summary and the connected WhatsApp number/status.
 */
export async function getOnboarding(): Promise<OnboardingData> {
  const res = await apiClient.get<{ data: OnboardingData }>('/onboarding')
  return res.data.data
}

/** Marks the caller's first run complete. It does NOT publish the agent —
 *  that is the operator's "Publicar", where channel + subscription + payment
 *  are enforced in one place. The old behavior (flipping draft bots live from
 *  here) was the pay-before-service bypass. */
export async function activateAssistant(): Promise<void> {
  await apiClient.post('/onboarding/activate', {})
}
